import React, { useEffect, useState } from 'react';
import { Menu, X, ArrowRight } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { NAV_LINKS } from '../mock';

const Logo = () => (
  <div className="flex items-center gap-2.5">
    <img
      src="/scatura-constellation.png"
      alt="Scatura"
      className="w-12 h-12 rounded-xl object-contain bg-[#020617]"
    />
    <div className="leading-tight">
      <div className="font-display text-[16px] font-semibold text-slate-900 tracking-tight">
        Scatura
      </div>
      <div className="text-[10px] text-slate-500 uppercase tracking-[0.18em] font-medium">
        Solutions
      </div>
    </div>
  </div>
);

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';

  // Resolve nav link: hash links from non-home go to '/#section'; route links stay as-is
  const resolveHref = (href) => {
    if (href.startsWith('#')) return isHome ? href : `/${href}`;
    return href;
  };

  const renderNavLink = (l, className, extraOnClick) => {
    const isRoute = l.href.startsWith('/');
    if (isRoute) {
      return (
        <Link
          key={l.label}
          to={l.href}
          onClick={extraOnClick}
          className={className}
          data-testid={`nav-link-${l.label.toLowerCase()}`}
        >
          {l.label}
        </Link>
      );
    }
    return (
      <a
        key={l.label}
        href={resolveHref(l.href)}
        onClick={extraOnClick}
        className={className}
        data-testid={`nav-link-${l.label.toLowerCase()}`}
      >
        {l.label}
      </a>
    );
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
    // setScrolled is a stable useState setter; onScroll is captured locally.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <header className="fixed top-4 left-0 right-0 z-50 flex justify-center px-4">
      <div
        className={`w-full max-w-6xl rounded-2xl border border-slate-200/70 transition-all duration-300 ${
          scrolled ? 'bg-white/85 backdrop-blur-xl shadow-lg shadow-slate-900/5' : 'bg-white/70 backdrop-blur-md'
        }`}
      >
        <div className="h-14 flex items-center justify-between px-4 md:px-5">
          <a href={isHome ? '#home' : '/'} className="flex items-center">
            <Logo />
          </a>

          <nav className="hidden lg:flex items-center gap-1">
            {NAV_LINKS.map((l) =>
              renderNavLink(
                l,
                'px-3 py-1.5 rounded-lg text-[13.5px] font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-colors'
              )
            )}
          </nav>

          <div className="flex items-center gap-2">
            <a
              href={resolveHref('#contact')}
              className="hidden md:inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-slate-900 text-white text-[13.5px] font-medium hover:bg-slate-800 transition-colors"
            >
              Book a call
              <ArrowRight size={14} />
            </a>
            <button
              className="lg:hidden p-2 text-slate-700"
              onClick={() => setOpen((v) => !v)}
              aria-label="Toggle menu"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {open && (
          <div className="lg:hidden border-t border-slate-200/70 px-4 py-3">
            <div className="flex flex-col gap-1">
              {NAV_LINKS.map((l) =>
                renderNavLink(
                  l,
                  'px-3 py-2 rounded-lg text-[14px] text-slate-700 hover:bg-slate-100',
                  () => setOpen(false)
                )
              )}
              <a
                href={resolveHref('#contact')}
                onClick={() => setOpen(false)}
                className="mt-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg bg-slate-900 text-white text-[14px] font-medium"
              >
                Book a call <ArrowRight size={14} />
              </a>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Navbar;
